import { useEffect } from "react";

const SITE_URL = process.env.REACT_APP_SITE_URL || "";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.jpg`;

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEO hook — sets title, description, canonical, OG/Twitter meta, and injects JSON-LD.
 * jsonLd: array of schema objects.
 * ogTitle/ogDescription: optional overrides for social cards (if omitted, falls back to title/description).
 */
export default function useSeo({ title, description, path, image, ogTitle, ogDescription, jsonLd = [] } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("description", description);
    const canonical = SITE_URL + (path || window.location.pathname);
    setLink("canonical", canonical);
    const socialTitle = ogTitle || title;
    const socialDesc = ogDescription || description;
    // Open Graph
    setMeta("og:title", socialTitle, "property");
    setMeta("og:description", socialDesc, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", image || DEFAULT_OG_IMAGE, "property");
    setMeta("og:site_name", "SSK Enterprises", "property");
    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:url", canonical);
    setMeta("twitter:title", socialTitle);
    setMeta("twitter:description", socialDesc);
    setMeta("twitter:image", image || DEFAULT_OG_IMAGE);

    // JSON-LD scripts (namespaced so we can clean up on unmount)
    const scripts = jsonLd
      .filter(Boolean)
      .map((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-page", "1");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
        return s;
      });

    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, JSON.stringify(jsonLd)]);
}
